import React, { useContext, useMemo } from 'react';
import UserContext from '../Context/UserContext';
import {
  makeStyles,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
} from '@fluentui/react-components';
import { CircleFilled } from '@fluentui/react-icons';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

/**
 * @param color - Hex code colour (E.g. #FF0
 */
const determineFontToken = (color: `#${string}`): string => {
  const red = parseInt(color.substring(1, 3), 16);
  const green = parseInt(color.substring(3, 5), 16);
  const blue = parseInt(color.substring(5, 7), 16);

  const brightness = red * 0.2126 + green * 0.7152 + blue * 0.0722;

  // Use white text if the brightness is dark (< 128), otherwise use black text
  if (brightness > 128) {
    return p4Tokens.colorNeutralForeground1Static; // Black text
  }
  return p4Tokens.colorNeutralForegroundStaticInverted; // White text
};

const useStyles = makeStyles({
  menuContainer: {
    float: 'left',
    marginLeft: '15px',
    padding: '14px 0 0 0',
    lineHeight: 'inherit',
  },
  menuButton: {
    color: '#FFFFFF',
    padding: '6px 8px 6px 11px',
  },
});

export const CompanySelector = ({ onCompanyClick }: { onCompanyClick: (id: number) => void }) => {
  const { currentUser } = useContext(UserContext);
  const company = currentUser.currentCompany;
  const styles = useStyles();

  const companies = useMemo(() => {
    return currentUser.systemUser.companies
      ?.filter((company) => company?.enabled === true)
      .map((company) => {
        return (
          <MenuItem
            onClick={() => {
              onCompanyClick(company!.id);
            }}
            icon={<CircleFilled color={company!.colourChoice ?? p4Tokens.colorBrandBackground} />}
          >
            {company!.name!}
          </MenuItem>
        );
      });
  }, [currentUser.systemUser.companies, onCompanyClick]);

  return (
    <Tooltip content={'Switch company'} relationship={'label'} positioning={'after'}>
      <div className={styles.menuContainer}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton
              className={styles.menuButton}
              style={{
                backgroundColor: company?.colourChoice || undefined,
                color: company?.colourChoice ? determineFontToken(company.colourChoice as `#${string}`) : undefined,
              }}
              appearance={'primary'}
            >
              {company?.name ?? 'Select a company'}
            </MenuButton>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>{companies}</MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </Tooltip>
  );
};
